import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";

import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import ForumRegistrationModal from "./components/modals/ForumRegistrationModal";
import AuthModal from "./components/modals/AuthModal";
import WelcomePopup from "./components/modals/WelcomePopup";

import Home from "./pages/Home";
import Evenement from "./pages/Evenement";
import Programme from "./pages/Programme";
import { SpeakersGrid, SpeakerProfile } from "./pages/Speakers";
import { ExhibitorDirectory, ExhibitorProfile } from "./pages/ExhibitorDirectory";
import Exposant from "./pages/Exposant";
import StandReservation from "./pages/StandReservation";
import Partenaires from "./pages/Partenaires";
import Medias from "./pages/Medias";
import News from "./pages/News";
import Participate from "./pages/Participate";

import AdminLayout from "./pages/admin/AdminLayout";
import DashboardOverview from "./pages/admin/DashboardOverview";
import ManageIntervenants from "./pages/admin/ManageIntervenants";
import ManageExposants from "./pages/admin/ManageExposants";
import ManageStands from "./pages/admin/ManageStands";
import ManageReservations from "./pages/admin/ManageReservations";
import ManagePartenaires from "./pages/admin/ManagePartenaires";
import ManageProgramme from "./pages/admin/ManageProgramme";
import ManageInscriptions from "./pages/admin/ManageInscriptions";
import ManageNews from "./pages/admin/ManageNews";
import ManageGallery from "./pages/admin/ManageGallery";
import ManageMessages from "./pages/admin/ManageMessages";
import ManageSettings from "./pages/admin/ManageSettings";
import ManagePages from "./pages/admin/ManagePages";
import PageEditor from "./pages/admin/PageEditor";

function AppContent() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isHomeRoute = location.pathname === '/';
  const [forumModalOpen, setForumModalOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const openForum = () => setForumModalOpen(true);

  // Scroll-to-hash on navigation (supports anchored sub-menu links like /evenement#objectifs)
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      // small delay so target sections mount before we scroll
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
      return () => clearTimeout(t);
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [location.pathname, location.hash]);

  return (
    <>
      {!isAdminRoute && <Navbar onOpenAuthModal={() => setAuthModalOpen(true)} />}
      <main>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Home onOpenForumModal={openForum} />} />
          <Route path="/evenement" element={<Evenement />} />
          <Route path="/programme" element={<Programme />} />
          <Route path="/intervenants" element={<SpeakersGrid />} />
          <Route path="/intervenants/:id" element={<SpeakerProfile />} />
          <Route path="/exposition/repertoire" element={<ExhibitorDirectory />} />
          <Route path="/exposition/repertoire/:id" element={<ExhibitorProfile />} />
          <Route path="/exposition/devenir-exposant" element={<Exposant onOpenForumModal={openForum} />} />
          <Route path="/exposition/stands" element={<StandReservation />} />
          <Route path="/partenaires" element={<Partenaires />} />
          <Route path="/medias" element={<Medias />} />
          <Route path="/actualites" element={<News />} />
          <Route path="/participer" element={<Participate onOpenForumModal={openForum} />} />

          {/* Admin routes */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<DashboardOverview />} />
            <Route path="intervenants" element={<ManageIntervenants />} />
            <Route path="exposants" element={<ManageExposants />} />
            <Route path="stands" element={<ManageStands />} />
            <Route path="reservations" element={<ManageReservations />} />
            <Route path="partenaires" element={<ManagePartenaires />} />
            <Route path="programme" element={<ManageProgramme />} />
            <Route path="inscriptions" element={<ManageInscriptions />} />
            <Route path="actualites" element={<ManageNews />} />
            <Route path="galerie" element={<ManageGallery />} />
            <Route path="messages" element={<ManageMessages />} />
            <Route path="parametres" element={<ManageSettings />} />
            <Route path="site-builder" element={<ManagePages />} />
            <Route path="site-builder/:pageId" element={<PageEditor />} />
          </Route>
        </Routes>
      </main>
      {!isAdminRoute && <Footer onOpenAuthModal={() => setAuthModalOpen(true)} />}
      <ForumRegistrationModal open={forumModalOpen} onOpenChange={setForumModalOpen} />
      <AuthModal open={authModalOpen} onOpenChange={setAuthModalOpen} />
      {isHomeRoute && <WelcomePopup />}
      <Toaster position="top-right" richColors />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
